import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { modalPrimaryButton } from "../../../components/ui/buttonStyles.js";
import { AssetModalField } from "../../Accounts/components/AssetModalField.jsx";
import { formatColumnLabel } from "../../Accounts/assetUtils.js";
import {
  DEVICE_FORM_EMPTY,
  deviceFormToPayload,
  deviceToForm,
  validateDeviceStep,
} from "./deviceUtils.js";
import { SimSelectField } from "../../Sims/components/SimSelectField.jsx";

const STEP_SUBTITLES = {
  1: "Step 1 — Device & connectivity",
  2: "Step 2 — Device field details",
};

export function CreateDeviceModal({
  open,
  onClose,
  onSave,
  editing = null,
  saving = false,
  fixedAccountId = null,
  deviceTypes = [],
  sims = [],
}) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...DEVICE_FORM_EMPTY, values: {} });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(editing);

  const activeDeviceTypes = useMemo(
    () => deviceTypes.filter((type) => type.status !== "deprecated"),
    [deviceTypes],
  );

  const selectedDeviceType = useMemo(
    () => deviceTypes.find((type) => type.id === form.deviceTypeId) ?? null,
    [deviceTypes, form.deviceTypeId],
  );

  const selectedSim = useMemo(
    () => sims.find((sim) => sim.id === form.simId) ?? null,
    [sims, form.simId],
  );

  const schemaColumns = selectedDeviceType?.columns ?? [];
  const totalSteps = schemaColumns.length > 0 ? 2 : 1;

  useLayoutEffect(() => {
    if (!open) return;
    setStep(1);
    const base = deviceToForm(editing);
    setForm({ ...base, accountId: fixedAccountId ?? base.accountId ?? "", values: base.values ?? {} });
    setErrors({});
    setSubmitError(null);
  }, [open, editing, fixedAccountId]);

  if (!open) return null;

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setValue = (key, value) => {
    setForm((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: value },
    }));
  };

  const handleDeviceTypeChange = (deviceTypeId) => {
    const nextType = deviceTypes.find((type) => type.id === deviceTypeId);
    const nextValues = {};
    (nextType?.columns ?? []).forEach((col) => {
      nextValues[col.key] = form.values?.[col.key] ?? "";
    });
    setForm((prev) => ({ ...prev, deviceTypeId, values: nextValues }));
  };

  const handleNext = () => {
    const nextErrors = validateDeviceStep(form, 1, selectedDeviceType, isEdit);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (totalSteps === 1) {
      handleSubmit();
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    if (step === 1) {
      onClose();
      return;
    }
    setStep(1);
  };

  const handleSubmit = async () => {
    const nextErrors = validateDeviceStep(form, 1, selectedDeviceType, isEdit);
    if (totalSteps === 2) {
      Object.assign(nextErrors, validateDeviceStep(form, 2, selectedDeviceType, isEdit));
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitError(null);
    try {
      const payload = deviceFormToPayload(
        fixedAccountId ? { ...form, accountId: fixedAccountId } : form,
        selectedDeviceType,
        selectedSim,
      );
      await onSave(payload);
      onClose();
    } catch (err) {
      setSubmitError(err.message ?? "Failed to save device");
    }
  };

  const typeOptions = activeDeviceTypes.map((type) => ({ value: type.id, label: type.name }));

  const subtitle = isEdit
    ? editing.deviceId
    : fixedAccountId
      ? "Add a new device to this account"
      : "Add a new device";

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-modal-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(620px, 96vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 16,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          boxShadow: ap.cardShadowLg,
          zIndex: 71,
        }}
      >
        <ModalShellHeader
          title={isEdit ? "Edit device" : "Create device"}
          titleId="device-modal-title"
          subtitle={step === 1 ? subtitle : STEP_SUBTITLES[step]}
          icon={Ico.device}
          onClose={onClose}
          closeLabel="Close device form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
          trailing={totalSteps > 1 ? (
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: A, background: `${A}18`, border: `1px solid ${A}33`, borderRadius: 999, padding: "6px 10px" }}>
              {step} / {totalSteps}
            </div>
          ) : null}
        />

        <div style={{ padding: 24, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {step === 1 && (
            <>
              {!isEdit && (
                <AssetModalField
                  label="Device ID"
                  value={form.deviceId}
                  onChange={(value) => set("deviceId", value.toUpperCase())}
                  placeholder="Enter device ID"
                  error={errors.deviceId}
                  icon="device"
                />
              )}
              <AssetModalField
                label="Device type"
                value={form.deviceTypeId}
                onChange={handleDeviceTypeChange}
                placeholder="Select device type"
                error={errors.deviceTypeId}
                icon="model"
                as="select"
                options={typeOptions}
                disabled={isEdit}
              />
              {activeDeviceTypes.length === 0 && (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>
                  Create a device type first to define fields for new devices.
                </p>
              )}
              <SimSelectField
                sims={sims}
                value={form.simId}
                onChange={(simId) => set("simId", simId)}
                error={errors.simId}
              />
              {sims.filter((sim) => sim.status === "active").length === 0 && (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>
                  Create a SIM card first in the SIM inventory.
                </p>
              )}
            </>
          )}

          {step === 2 && (
            <>
              {schemaColumns.map((col) => (
                <AssetModalField
                  key={col.key}
                  label={formatColumnLabel(col.key)}
                  value={form.values?.[col.key] ?? ""}
                  onChange={(value) => setValue(col.key, value)}
                  placeholder={`Enter ${formatColumnLabel(col.key).toLowerCase()}`}
                  error={errors.values?.[col.key]}
                  icon={col.type === "int" ? "grid" : col.type === "file" ? "layers" : "model"}
                  type={col.type === "int" ? "number" : "text"}
                />
              ))}
            </>
          )}

          {submitError && (
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="button" onClick={handleBack} disabled={saving} style={outlineBtn(c, dark)}>
              {step === 1 ? "Cancel" : "Back"}
            </button>
            {step < totalSteps ? (
              <button type="button" onClick={handleNext} style={modalPrimaryButton(A, dark)}>
                {totalSteps === 1 ? "Create device" : "Next"}
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={saving} style={modalPrimaryButton(A, dark, saving)}>
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create device"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function outlineBtn(c, dark) {
  return {
    height: 44,
    padding: "0 20px",
    borderRadius: 10,
    border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    background: "transparent",
    color: c.text,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  };
}

